import type { BetslipStatus } from '../../../types/betslip';
import type { MatchStatus } from '../../../types/match';
import { type ObjectValuesUnionType } from '../../../utils/typing';

import type { GridsActionType } from './actions';

export const GridMatchLengths = Object.freeze({
  SEVEN: 7,
  TWELVE: 12,
});

export type GridMatchLength = ObjectValuesUnionType<typeof GridMatchLengths>;

export const GridsCheckTypes = Object.freeze({
  HOME: '1',
  NULL: 'N',
  EXT: '2',
});

export type GridsCheckType = ObjectValuesUnionType<typeof GridsCheckTypes>;

export const CheckIndexes = Object.freeze({
  [GridsCheckTypes.HOME]: 0,
  [GridsCheckTypes.NULL]: 1,
  [GridsCheckTypes.EXT]: 2,
});

export const SelectionCheckTypes = Object.freeze({
  UNCHECK: '0',
  CHECK: '1',
  BASE: '2',
});

export type SelectionCheckType = ObjectValuesUnionType<typeof SelectionCheckTypes>;

export type ChecksTypes = {
  [gridsCheckType in GridsCheckType]: boolean;
};

export type GridsAction = {
  type: GridsActionType;
  payload: unknown;
};

export const GridsErrorCodes = Object.freeze({
  ACCESS_DENIED: 1,
  NOT_ENOUGH_MONEY: 100,
  INVALID_BET_AMOUNT: 101,
  MULTIPLE_CHECKS_REQUIRED: 102,
  MODERATOR_REACHED: 400,
  DAILY_MODERATOR_REACHED: 401,
  WEEKLY_MODERATOR_REACHED: 402,
  MONTHLY_MODERATOR_REACHED: 403,
  GRID_NOT_AVAILABLE: 301,
  GRID_INCOMPLETE: 302,
});

export type GridsErrorCode = ObjectValuesUnionType<typeof GridsErrorCodes>;

export const PoolStatuses = Object.freeze({
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED',
  LIVE: 'LIVE',
});

export type PoolStatus = ObjectValuesUnionType<typeof PoolStatuses>;

export const GridStatuses = Object.freeze({
  RUNNING: 'RUNNING',
  NOT_FUNDED: 'NOT_FUNDED',
  CREATING: 'CREATING',
  SETTLED: 'SETTLED',
  FUNDING: 'FUNDING',
});

export type GridStatus = ObjectValuesUnionType<typeof GridStatuses>;

export const GridTypes = Object.freeze({
  PERSONAL: 1,
  TEAM: 2,
});

export type GridType = ObjectValuesUnionType<typeof GridTypes>;

export const HistoryFilters = Object.freeze({
  ALL: 0,
  RUNNING: 7,
  CANCELLED: 6,
  WON: 4,
  LOST: 5,
});

export type HistoryFilter = ObjectValuesUnionType<typeof HistoryFilters>;

export const PublicGridFilters = Object.freeze({
  MOST_COMPLETE: '[most-complete]',
  MOST_EXPENSIVE: '[most-expensive]',
  LESS_EXPENSIVE: '[less-expensive]',
  MOST_POPULAR: '[most-popular]',
  MOST_RECENT: '[most-recent]',
  BEST_AUTHOR_WINNINGS: '[best-winnings]',
  BEST_AUTHOR_BADGES: '[best-badges]',
});

export type PublicGridFilter = ObjectValuesUnionType<typeof PublicGridFilters>;

export type SystemGridWithAmount = { selection: string; amount: number; selectionIndex: number };

export type Results = {
  label: number;
  value: number | null;
  isHighlight?: boolean;
};

export type GridBetslip = {
  validationDate: number;
  betslipStatus: BetslipStatus;
  betslipReference: string;
  betAmount: number;
  winningsAmount: number | null;
  nickname: string;
  avatarPath: string;
};

export type GridsAuthor = {
  userId: number;
  nickname: string;
  avatarPath: string | null;
  badges?: Array<number>;
  winnings?: {
    GridMatchLength: number;
  };
};

export type Contributor = {
  nickname: string;
  avatarPath: string;
  date: number;
  amount: number;
};

export type GridState = {
  gridReference: string;
  gridType: GridType;
  gridStatus: GridStatus;
  previousGridStatus: GridStatus;
  poolId: number;
  gridSelection: number; // ?
  strGridSelection: string;
  nbDoubles: number;
  nbTriples: number;
  nbIndividualGrids: number;
  isPrivate: number;
  isSystem: number;
  allBetAmount: number;
  allWinningsAmount: number;
  betslips: Array<GridBetslip>;
  author: GridsAuthor;
  systemStrGridSelections?: Array<string>;
  systemGridSelections?: Array<number>;
};

export type GridMatch = {
  matchId: number;
  status: MatchStatus;
  matchStart: number;
  competitor1Id: number;
  competitor2Id: number;
  competitor1Name: string;
  competitor2Name: string;
  score1?: number;
  score2?: number;
  odds?: {
    [gridsCheckType in GridsCheckType]: number;
  };
};

export type Payout = {
  nbCorrectResults: number;
  winningGrids: number;
  winningsPerGrid?: number;
};

export type GridsPoolId = number;

export type GridsPoolState = {
  poolId: number;
  poolStatus: PoolStatus;
  poolTitle: string;
  poolEnd: number;
  prizepoolAmount: number;
  guaranteedAmount: number;
  netStakes: number;
  distributedAmount: number;
  postponedAmount: number | null; // ??
  addedAmount: number | null;
  strPoolResult: string;
  matches: Array<number>;
  payout?: Array<Payout>;
  repart?: Array<number>;
  isFetching?: boolean;
  nbPublicGrids: number;
  publicGrids?: Array<string>;
  nbMatches?: number;
};

export type BasketState = {
  poolId: number | null;
  gridReference: string | null;
  gridType: GridType;
  betAmount: string;
  isPrivate: boolean;
  isSystem: boolean;
  isHistory: boolean;
  selections: Array<string>;
  isSubmitting: boolean;
  isValidated: boolean;
  estr: string | null;
};

export type PublicGridsState = {
  poolId: number | null;
  isFetching: boolean;
  filter: PublicGridFilter;
  currentPage: number;
};

export type HistoryState = {
  gridCount: number | null;
  isFetching: boolean;
  currentPage: number;
  filter: HistoryFilter;
  gridReferences: Array<string>;
};

export type UiState = {
  tutoStep: number;
  poolNavigation: GridMatchLength;
  activePools: Array<number> | null;
  runningGrids: Array<string> | null;
};

export type GridsState = {
  basket: BasketState;
  history: HistoryState;
  publicGrids: PublicGridsState;
  ui: UiState;
};
